/**
 * Saved cards.
 *
 * Nothing here ever sees a card number. The backend stores a gateway token and returns only what
 * is needed to tell one card from another — brand, last four, expiry — and the hold is placed
 * server-side at submit time. The payment step of the wizard records *which* card the draft will
 * use and calls no gateway at all.
 */
import { apiRequest } from '@/shared/api/client';

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

export function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  return apiRequest<PaymentMethod[]>('/v1/consumer/payment-methods');
}

/** «Visa · •••• 4242» — the line the summary reads straight off the draft. */
export function paymentMethodLabel(method: PaymentMethod): string {
  return `${method.brand} · •••• ${method.last4}`;
}

/**
 * Two digits, because that is how it is printed on the card.
 *
 * `exp_year` arrives as a full year; showing «2028» next to «09» reads as a different kind of
 * number than the «09/28» somebody is checking against the plastic in their hand.
 */
export function paymentMethodExpiry(method: PaymentMethod): string {
  const month = String(method.exp_month).padStart(2, '0');
  const year = String(method.exp_year % 100).padStart(2, '0');
  return `${month}/${year}`;
}

/**
 * Whether the card is past its printed expiry.
 *
 * A card expires at the **end** of its month, so September 2028 is good through 30 September.
 * Comparing against the first of the month would grey out a card that still works for four weeks.
 */
export function isExpired(method: PaymentMethod, now: Date = new Date()): boolean {
  const endOfMonth = new Date(Date.UTC(method.exp_year, method.exp_month, 1));
  return now.getTime() >= endOfMonth.getTime();
}

/**
 * Removes a saved card.
 *
 * The backend refuses one attached to a live hold — that is the case worth surfacing verbatim,
 * because finishing or cancelling the order is something the person can actually do.
 */
export function deletePaymentMethod(id: string): Promise<void> {
  return apiRequest<void>(`/v1/consumer/payment-methods/${id}`, { method: 'DELETE' });
}

/** The card a new order pre-selects. Exactly one is the default at any time; the server enforces it. */
export function makeDefaultPaymentMethod(id: string): Promise<void> {
  return apiRequest<void>(`/v1/consumer/payment-methods/${id}/make-default`, { method: 'POST' });
}

/**
 * What links a card.
 *
 * Note what this cannot hold: there is no field capable of carrying a full card number.
 * `gateway_method_id` is an opaque token the gateway's own widget issues; the rest is display
 * metadata it hands back. `gateway_code` is decided server-side and deliberately not accepted
 * here — a caller claiming an arbitrary one would create rows the payment layer can never resolve.
 */
export interface CreatePaymentMethodInput {
  gateway_method_id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

/** The first card added becomes the default automatically. */
export function createPaymentMethod(input: CreatePaymentMethodInput): Promise<PaymentMethod> {
  return apiRequest<PaymentMethod>('/v1/consumer/payment-methods', {
    method: 'POST',
    body: input,
  });
}

/**
 * Linking a real card, in two calls.
 *
 * The card itself is typed on the BANK's page, never here — that is what keeps a card number out
 * of this origin entirely. We ask the backend to open a session, send the browser to `form_url`,
 * and the bank returns the payer to `/profile/payment-methods/return?session=…`, where the second
 * call asks what actually happened.
 *
 * The return URL is never trusted for the outcome: the backend re-reads the order from the gateway.
 * A payer who edits that URL, or replays it from history, cannot conjure a card.
 */
export interface StartBindingResponse {
  session_id: string;
  /** The bank's hosted card form. Assign to `location.href` — it is a different origin. */
  form_url: string;
}

export function startCardBinding(): Promise<StartBindingResponse> {
  return apiRequest<StartBindingResponse>('/v1/consumer/payment-methods/binding-sessions', {
    method: 'POST',
  });
}

/**
 * `pending` is a real answer, not a failure: the payer may land back here having closed the bank's
 * page mid-3-D-Secure, and the attempt can still complete. The UI offers a retry rather than
 * declaring the card dead.
 */
export type BindingStatus = 'completed' | 'pending' | 'failed';

export interface CompleteBindingResponse {
  status: BindingStatus;
  payment_method: PaymentMethod | null;
  /** The gateway's own wording for a refusal — written for the payer, safe to show verbatim. */
  message: string | null;
}

export function completeCardBinding(sessionId: string): Promise<CompleteBindingResponse> {
  return apiRequest<CompleteBindingResponse>(
    `/v1/consumer/payment-methods/binding-sessions/${sessionId}/complete`,
    { method: 'POST' },
  );
}
